import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { RangeSpec } from "../specs.ts";

/** Digits, a leading minus and (when decimal) one point; anything else typed is dropped. */
export function sanitizeNumericInput(raw: string, decimal: boolean): string {
  const cleaned = raw.replace(decimal ? /[^\d.-]/g : /[^\d-]/g, "");
  if (cleaned.length <= 1) return cleaned;
  const signed = cleaned[0] + cleaned.slice(1).replace(/-/g, "");
  const point = signed.indexOf(".");
  if (point === -1) return signed;
  return signed.slice(0, point + 1) + signed.slice(point + 1).replace(/\./g, "");
}

interface RangeEditorProps {
  spec: RangeSpec;
  min: string;
  max: string;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
}

/** Min and max (or just "at least") number inputs. */
export default function RangeEditor({ spec, min, max, onMinChange, onMaxChange }: RangeEditorProps) {
  const decimal = !!spec.decimal;
  const slotProps = {
    htmlInput: { inputMode: decimal ? ("decimal" as const) : ("numeric" as const) },
    input: spec.unit
      ? {
          endAdornment: (
            <InputAdornment position="end">
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {spec.unit}
              </Typography>
            </InputAdornment>
          ),
        }
      : undefined,
  };
  return (
    <Box sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
      <TextField
        size="small"
        label={spec.maxParam ? "Min" : "At least"}
        value={min}
        onChange={(e) => onMinChange(sanitizeNumericInput(e.target.value, decimal))}
        autoFocus
        slotProps={slotProps}
        sx={{ flex: 1 }}
      />
      {spec.maxParam && (
        <>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            –
          </Typography>
          <TextField
            size="small"
            label="Max"
            value={max}
            onChange={(e) => onMaxChange(sanitizeNumericInput(e.target.value, decimal))}
            slotProps={slotProps}
            sx={{ flex: 1 }}
          />
        </>
      )}
    </Box>
  );
}
